import { useMutation } from '@apollo/client'
import { Autocomplete, Popover, TextField } from '@mui/material'
import { RosterSourceStatus } from '__generated__/graphql'
import { FC } from 'react'
import { getSourceStatusLabel } from 'recruiter/components/SourceStatusPill'
import { GetCandidatesForRosterSql } from 'recruiter/contexts'
import { UpdateSourceStatus } from 'recruiter/graphql/updateSourceStatus.gql'
import { useBulkActionContext } from 'recruiter/hooks/useBulkActionContext'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem } from 'shared/utils'

interface EditSourceStatusProps {
    onClose: () => void
    rosterId: string
    candidateIds: string[]
    isOpen: boolean
    anchorRef: React.RefObject<HTMLButtonElement>
}

const EditSourceStatus: FC<EditSourceStatusProps> = ({
    rosterId,
    candidateIds,
    isOpen,
    anchorRef,
    onClose,
}) => {
    const toast = useToast()
    const { setLoading } = useBulkActionContext()
    const [updateSourceStatus] = useMutation(UpdateSourceStatus, {
        refetchQueries: [GetCandidatesForRosterSql],
    })

    return (
        <>
            <Popover
                open={isOpen}
                anchorEl={anchorRef.current}
                onClose={onClose}
                slotProps={{
                    paper: {
                        sx: {
                            marginTop: getPxAsRem(32),
                        },
                    },
                }}
            >
                <Autocomplete
                    disableClearable
                    fullWidth
                    sx={{ width: getPxAsRem(224) }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            placeholder="Select Source Status"
                        />
                    )}
                    onChange={async (_event, value) => {
                        setLoading(true)
                        const { data } = await updateSourceStatus({
                            variables: {
                                rosterId,
                                candidateIds,
                                sourceStatus: value,
                            },
                        })
                        if (data?.updateRosterCandidateSourceStatus.success) {
                            toast({
                                title: 'Success',
                                message:
                                    data?.updateRosterCandidateSourceStatus
                                        .message ||
                                    'Candidate source status updated successfully',
                                severity: 'success',
                            })
                        } else {
                            toast({
                                title: 'Error',
                                message:
                                    data?.updateRosterCandidateSourceStatus
                                        .message ||
                                    'Error updating source status',
                                severity: 'error',
                            })
                        }
                        onClose()
                        setLoading(false)
                    }}
                    options={Object.values(RosterSourceStatus)}
                    getOptionLabel={(option) => getSourceStatusLabel(option)}
                />
            </Popover>
        </>
    )
}

export default EditSourceStatus
